import * as Fonts from "@/constants/calendar-fonts";
import FontService from "@/services/font-service";
import Utils from "@/utils";

/**
 * Test utility to calibrate font width adjustments for different fonts
 */
export default {
    /**
     * Run calibration tests for all fonts with sample text
     * @param {Array} sampleTexts - Array of text samples to test
     * @returns {Object} - Calibration results
     */
    runCalibration(
        sampleTexts = [
            "November Birthday Dessert Bar (DR)",
            "Meeting with John"
        ]
    ) {
        const results = {};
        const fontSizes = [7, 8, 9, 10, 11, 12];

        // Get all fonts from the constants
        const allFonts = Object.values(Fonts).filter(
            (font) => typeof font === "string"
        );

        // Test each font
        allFonts.forEach((font) => {
            results[font] = { sizes: {} };

            // Test each font size
            fontSizes.forEach((size) => {
                const sizeResults = this.testFontSize(font, size, sampleTexts);
                results[font].sizes[size] = sizeResults;
            });

            // Calculate recommended adjustment
            results[font].recommendedAdjustment =
                this.calculateRecommendedAdjustment(results[font].sizes);
        });

        return results;
    },

    /**
     * Test a specific font and size with sample texts
     * @param {string} font - Font name
     * @param {number} fontSize - Font size
     * @param {Array} sampleTexts - Array of text samples
     * @returns {Object} - Test results
     */
    testFontSize(font, fontSize, sampleTexts) {
        const result = {
            calculatedWidths: {},
            renderedWidths: {},
            differences: {},
            averageDifference: 0
        };

        // Get font weight for this font
        const fontWeight = FontService.getFontWeightFromCSS(font);

        // Create a hidden element for rendering
        const testElement = document.createElement("span");
        testElement.style.fontFamily = font;
        testElement.style.fontSize = `${fontSize}pt`;
        testElement.style.fontWeight = fontWeight || "normal";
        testElement.style.position = "absolute";
        testElement.style.visibility = "hidden";
        testElement.style.whiteSpace = "nowrap";
        document.body.appendChild(testElement);

        // Test each sample text
        sampleTexts.forEach((text) => {
            // Calculate width using Utils.getTextWidth
            const calculatedWidth = Utils.getTextWidth(
                text,
                font,
                fontSize,
                fontWeight
            );

            // Measure actual rendered width
            testElement.textContent = text;
            const renderedWidth = testElement.offsetWidth;

            // Calculate difference
            const difference = renderedWidth - calculatedWidth;

            result.calculatedWidths[text] = calculatedWidth;
            result.renderedWidths[text] = renderedWidth;
            result.differences[text] = difference;
        });

        // Calculate average difference
        let sum = 0;
        let count = 0;
        Object.values(result.differences).forEach((diff) => {
            sum += diff;
            count++;
        });
        result.averageDifference = count > 0 ? sum / count : 0;

        // Remove the test element
        document.body.removeChild(testElement);

        return result;
    },

    /**
     * Calculate recommended adjustment based on test results
     * @param {Object} sizeResults - Results for different font sizes
     * @returns {number} - Recommended adjustment
     */
    calculateRecommendedAdjustment(sizeResults) {
        // Focus on font size 10 as it's most commonly used
        const size10Results = sizeResults[10] || {};
        const size9Results = sizeResults[9] || {};

        // Use size 10 if available, otherwise fall back to size 9
        const targetResults = size10Results.averageDifference
            ? size10Results
            : size9Results;

        // Calculate ceiling of average difference plus small safety margin
        return Math.ceil(targetResults.averageDifference + 2);
    },

    /**
     * Generate updated fontAdjustments object for calendar.js
     * @param {Object} calibrationResults - Results from runCalibration
     * @returns {Object} - Updated fontAdjustments object
     */
    generateFontAdjustments(calibrationResults) {
        const fontAdjustments = {};

        Object.keys(calibrationResults).forEach((font) => {
            const adjustment = calibrationResults[font].recommendedAdjustment;
            // Only add positive adjustments (negative would make text overflow)
            if (adjustment > 0) {
                fontAdjustments[font] = adjustment;
            }
        });

        console.log(" Recommended font adjustments:", fontAdjustments);
        return fontAdjustments;
    },

    generateSizeSpecificFontAdjustments(calibrationResults) {
        const fontSizeAdjustments = {
            7: {},
            8: {},
            9: {},
            10: {},
            11: {},
            12: {}
        };

        Object.keys(calibrationResults).forEach((font) => {
            const sizeResults = calibrationResults[font].sizes;

            // For each font size, calculate adjustment
            Object.keys(sizeResults).forEach((size) => {
                const targetResults = sizeResults[size];
                const adjustment = Math.ceil(
                    targetResults.averageDifference + 2
                );

                // Only add positive adjustments
                if (adjustment > 0) {
                    fontSizeAdjustments[size][font] = adjustment;
                }
            });
        });

        return fontSizeAdjustments;
    }
};
